import type { ActingFor } from '@myrewards/contracts';
import { useState, type ReactElement } from 'react';
import { ConfirmDialog, TextField, type ConfirmDialogProps } from '../ui';
import { ActForStep, type ActForDone, type ActForStepProps } from './ActForStep';
import { ForWhom, type ForWhomValue } from './ForWhom';

/** What a confirm offers when its action can be done for someone (APP-148 D20). */
export interface ActForChoice {
  /** "Who is this change for?", "Who is this for?". */
  question: string;
  /** "For an Executive", "For a Sector Lead (Graham Whitlock)". */
  someone: string;
  /** The step, as `ActForStep` takes it; the confirm adds its reason and its buttons' work. */
  step: Omit<ActForStepProps, 'children' | 'ready' | 'inline' | 'onCancel' | 'onConfirm'>;
  /** Saves the action for the person named, with the reason. Throw to keep the step open. */
  onConfirm: (reason: string, actingFor: ActingFor, done: ActForDone) => Promise<void>;
}

/**
 * The app's confirm (a summary and a reason) with "Just me / For ‹someone›" under the summary
 * (D20, the record's withdraw). "Just me" is the confirm as it always was. Choosing the other shows
 * the step in its place: who it's for, the same reason, files and a note, what will happen and how
 * it's recorded. Without `actFor` it's the plain confirm.
 */
export function ActForConfirm({
  actFor,
  ...dialog
}: ConfirmDialogProps & { actFor: ActForChoice | null }): ReactElement {
  const [who, setWho] = useState<ForWhomValue>('me');
  // One reason for "Just me" and the step: null until typed, then what was typed in either.
  const [typed, setTyped] = useState<string | null>(null);
  // While the step's confirm is saving, the dialog stays: a refusal still has somewhere to show.
  const [busy, setBusy] = useState(false);
  // Each opening starts afresh, however it was closed.
  const [opening, setOpening] = useState(0);
  if (actFor === null) return <ConfirmDialog {...dialog} />;

  const { intro, ...rest } = dialog;
  const reason = typed ?? dialog.initialReason ?? '';
  const required = dialog.reasonRequired ?? true;
  const reset = (): void => {
    setWho('me');
    setTyped(null);
    setBusy(false);
    setOpening((n) => n + 1);
  };
  const close = (): void => {
    reset();
    dialog.onOpenChange(false);
  };
  const acting = who === 'someone';

  return (
    <ConfirmDialog
      key={opening}
      {...rest}
      reason={reason}
      onReasonChange={setTyped}
      busy={busy}
      onOpenChange={(open) => {
        if (!open) reset();
        dialog.onOpenChange(open);
      }}
      // The step says what will happen, as its own lines, so the dialog's intro and summary go.
      {...(!acting && intro !== undefined && { intro })}
      summary={acting ? [] : dialog.summary}
      before={
        <ForWhom
          label={actFor.question}
          someone={actFor.someone}
          value={who}
          onChange={setWho}
          isDisabled={busy}
        />
      }
      takeover={
        acting ? (
          <ActForStep
            {...actFor.step}
            inline
            ready={!required || reason.trim() !== ''}
            onCancel={close}
            onConfirm={async (actingFor, done) => {
              setBusy(true);
              try {
                await actFor.onConfirm(reason.trim(), actingFor, done);
              } finally {
                setBusy(false);
              }
              close();
            }}
          >
            <TextField
              label={dialog.reasonLabel ?? 'Reason'}
              multiline
              value={reason}
              onChange={setTyped}
              isRequired={required}
            />
          </ActForStep>
        ) : undefined
      }
    />
  );
}
